import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { filter, map, Subject, takeUntil } from 'rxjs';
import { AuthenticationService } from '../shared/services/authentication.service';
import { MsalService } from '@azure/msal-angular';
import { StaticService } from '../shared/services/static.service';
import { CookieService } from 'ngx-cookie-service';

@Component({
  selector: 'app-layout-shell',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive, RouterOutlet],
  templateUrl: './layout-shell.component.html',
  styleUrls: ['./layout-shell.component.scss']
})
export class LayoutShellComponent implements OnInit, OnDestroy {
  screenItems = signal<{ key: string; label: string }[]>([]);
  activeKey = signal<string>('DASHBOARD');
  userName = signal<string>('');
  dashboardLabel = signal<string>('Dashboard');
  private readonly destroying$ = new Subject<void>();

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private auth: AuthenticationService,
    private msal: MsalService,
    private staticService: StaticService,
    private cookieService: CookieService
  ) {}

  ngOnInit(): void {
    this.cleanupMsalCookies(); // Clean up any legacy cookies
    this.loadScreens();
    this.userName.set(this.auth.currentUserName$.value ?? '');
    this.auth.currentUserName$
      .pipe(takeUntil(this.destroying$))
      .subscribe((u) => this.userName.set(u ?? ''));

    this.router.events
      .pipe(
        takeUntil(this.destroying$),
        filter((e) => e instanceof NavigationEnd),
        map(() => this.route),
        map((r) => {
          while (r.firstChild) r = r.firstChild;
          return r;
        }),
        map((r) => r.snapshot.params?.['name'] as string | undefined)
      )
      .subscribe((name) => this.activeKey.set(name ?? 'DASHBOARD'));
  }

  private loadScreens(): void {
    const cached = sessionStorage.getItem('SCREENS');
    if (cached) {
      try {
        const map = JSON.parse(cached) as Record<string, string>;
        if (map && typeof map['DASHBOARD'] === 'string') {
          this.dashboardLabel.set(map['DASHBOARD']);
        }
        const items = Object.entries(map ?? {})
          .map(([key, label]) => ({ key, label }))
          .filter((i) => i.key !== 'DASHBOARD');
        this.screenItems.set(items);
        return;
      } catch {}
    }
    this.staticService.getScreensMap().subscribe((map: Record<string, string>) => {
      if (map && typeof map['DASHBOARD'] === 'string') {
        this.dashboardLabel.set(map['DASHBOARD']);
      }
      const items = Object.entries(map ?? {})
        .map(([key, label]) => ({ key, label }))
        .filter((i) => i.key !== 'DASHBOARD');
      this.screenItems.set(items);
    });
  }

  trackByKey(_: number, item: { key: string }) {
    return item.key;
  }

  getLabelForKey(key: string): string {
    if (key === 'DASHBOARD') {
      return this.dashboardLabel();
    }
    const found = this.screenItems().find(i => i.key === key);
    return found?.label ?? key;
  }

  onLogout() {
    this.auth.logout();
    this.msal.logoutRedirect();
  }

  /**
   * Removes any legacy MSAL cookies (safeguard for migrated sessions)
   * MSAL tokens are stored in sessionStorage, not cookies
   */
  private cleanupMsalCookies(): void {
    try {
      const msalCookies = ['msal-access-token', 'msal-id-token'];
      const domain = this.getDomainFromURL(window.location.hostname);
      
      msalCookies.forEach(cookieName => {
        if (this.cookieService.check(cookieName)) {
          console.log(`Cleaning up legacy MSAL cookie: ${cookieName}`);
          
          // Delete cookie from current domain
          this.cookieService.delete(cookieName, '/');
          
          // Delete cookie from parent domain
          this.cookieService.delete(cookieName, '/', domain);
          
          // Delete cookie from root domain
          this.cookieService.delete(cookieName, '/', '');
        }
      });
    } catch (error) {
      console.error('Error cleaning up MSAL cookies:', error);
    }
  }

  /**
   * Extract parent domain from hostname
   */
  private getDomainFromURL(hostname: string): string {
    const parts = hostname.split('.');
    if (parts.length > 2) {
      return '.' + parts.slice(-2).join('.');
    }
    return hostname;
  }

  ngOnDestroy(): void {
    this.destroying$.next();
    this.destroying$.complete();
  }
}


